import { AuditDetails, Entity } from '../../entity';
import { ActivityEntityName } from '../../shared/activity.entity.name';
import { EmbeddedCode } from '../../shared/code';
import { PlannedWorkAttachment, VisitTaskChange } from '../planned.task/planned.task';

export type PlannedItemContent = EmbeddedCode;

/**
 * How an item line behaves when Job construction groups several Visits into one Job. Consumed only
 * by construction: approval and rescheduling never merge Visit records.
 */
export enum PlannedItemConstructionRule {
    KeepOne = 'KeepOne',
    AddQuantities = 'AddQuantities',
    KeepSeparate = 'KeepSeparate',
}

// Absent means KeepOne, so existing rows need no backfill and fingerprints stay stable.
export const DEFAULT_ITEM_CONSTRUCTION_RULE = PlannedItemConstructionRule.KeepOne;

/** Planner wording for each rule, worded for Job construction; the member names are storage vocabulary. */
export const PLANNED_ITEM_CONSTRUCTION_RULE_LABELS: Readonly<Record<PlannedItemConstructionRule, string>> = {
    [PlannedItemConstructionRule.KeepOne]: 'Keep one line',
    [PlannedItemConstructionRule.AddQuantities]: 'Add quantities together',
    [PlannedItemConstructionRule.KeepSeparate]: 'Keep a separate line for each Visit',
};

export function isItemConstructionRule(value: unknown): value is PlannedItemConstructionRule {

    return Object.values(PlannedItemConstructionRule).includes(value as PlannedItemConstructionRule);
}

export function effectiveItemConstructionRule(item: { constructionRule?: PlannedItemConstructionRule } | null | undefined): PlannedItemConstructionRule {

    const rule = item?.constructionRule;

    return isItemConstructionRule(rule) ? rule : DEFAULT_ITEM_CONSTRUCTION_RULE;
}
export interface PlannedItemConstructionFields {
    attachment: PlannedWorkAttachment;
    parentOccurrenceNodeId?: string;
}

/**
 * One catalogue line owned by a planned-work parent, or one Visit's exception to such a line.
 *
 * A single collection (`planned_items`) replaces the embedded `items` arrays on Service Lines and
 * their templates and the item half of Visit overrides. The parent pointer is what makes moves free:
 * a task carries its items to another line because the items point at the task, not the line.
 *
 * `scope: 'standing'` is the plan's own line. A Visit record is either Attached — the cancel marker
 * for one standing line — or Local, a line added on this Visit alone. A Visit never edits a standing
 * line's content; that is a plan decision and applies to every future Visit.
 */
export enum PlannedItemScope {
    Standing = 'standing',
    Visit = 'visit',
    NextN = 'nextN',
    DateRange = 'dateRange',
}

/** Owners that may carry standing items. Templates are parents too, so template items need no twin entity. */
export type PlannedItemParentType = ActivityEntityName.ServiceLine | ActivityEntityName.ServiceLineTask | ActivityEntityName.ServiceLineTemplate | ActivityEntityName.ServiceLineTaskTemplate;

export interface PlannedItemParent {
    type: PlannedItemParentType;
    _id: string;
}

export interface PlannedItemRange {
    from: Date;
    to: Date;
}

export interface PlannedItem extends PlannedItemContent, AuditDetails {
    /** Live plan owner. Absent for template parents. */
    servicePlanId?: string;
    /** Template owner. Absent for live parents. */
    servicePlanTemplateId?: string;
    parent: PlannedItemParent;
    scope: PlannedItemScope;
    /**
     * Sibling order within the parent, as a LexoRank.
     *
     * NOT `sortOrder`: `EmbeddedCode` already declares that as a NUMBER, and a planned item is an
     * `EmbeddedCode`. Reusing the name would have put a lex string into a numeric field, where it
     * would compare wrongly rather than fail loudly.
     */
    rank: string;
    /** Visit exceptions only. */
    visitId?: string;
    range?: PlannedItemRange;
    remaining?: number;
    attachment?: PlannedWorkAttachment;
    parentOccurrenceNodeId?: string;
    /** Visit exception: the standing line is left out of this Visit. */
    excluded?: boolean;
    /**
     * A Visit-local item's minted id from the SAME sequencer standing task schedules draw from.
     *
     * An IDENTITY, not a cadence. It exists so an item added on one Visit sits in that date's due-day
     * `pins` exactly as an added task does, and hydrates through the same `(date, siteId, scheduleId)`
     * join — one membership definition for both rather than a second, parallel mechanism for items.
     * Nothing projects it: every projection read is partial on `scope: standing`, and an item has no
     * schedule to project.
     */
    scheduleId?: number;
    /** Plan and template records only; a Visit write carrying one is refused. */
    constructionRule?: PlannedItemConstructionRule;
}

/**
 * Where an effective line came from — the grouping lane on a Visit's items board.
 *
 * Named `origin` on the row rather than `source`, because `EmbeddedCode.source` is already the
 * catalogue's own `Source` and a planned item is an `EmbeddedCode`.
 */
export interface PlannedItemOrigin {
    type: PlannedItemParentType;
    _id: string;
    name: string;
}

/**
 * A resolved line as the boards show it: the standing content, or a Local line's own, plus the
 * provenance and change state the row chips need. Standing-only reads (a Service Line's own Items tab)
 * return the same shape with no `visitId`, no `visitChange` and `origin` equal to the parent.
 */
export interface PlannedItemLine extends PlannedItemContent {
    attachment: PlannedWorkAttachment;
    parentOccurrenceNodeId?: string;
    parent: PlannedItemParent;
    origin: PlannedItemOrigin;
    scope: PlannedItemScope;
    rank: string;
    visitId?: string;
    /** The Visit exception record behind this row, when one exists. Restore and Remove act on it. */
    exceptionId?: string;
    visitChange?: VisitTaskChange;
    /**
     * This line exists only for this Visit — it has no standing record behind it.
     *
     * Separate from `visitChange` because the two answer different questions and a row can be both.
     * `visitChange` reports ONE badge, and Excluded outranks Added: a line the user added here and then
     * excluded reads `Excluded`, which is correct for the chip and useless for deciding what the row
     * menu may offer. Remove is only ever valid on an added line, so it needs the fact itself rather
     * than a badge that may be describing something else — and inferring it from `scope !== 'standing'`
     * makes every caller re-derive a rule the resolver already knows.
     */
    addedForVisit?: boolean;
    readonly?: boolean;
    constructionRule?: PlannedItemConstructionRule;
}

export enum PlannedItemCommandId {
    Exclude = 'PlannedItemExcludeCommand',
    Restore = 'PlannedItemRestoreCommand',
    Remove = 'PlannedItemRemoveCommand',
}

/**
 * A Visit row's editor identity: the Visit plus the standing line it stands for.
 *
 * Rows on a Visit board are NOT addressable by the standing line's own id. If they were, the editor
 * would open the recurring line and a save would write the plan — the exact mutation a Visit exception
 * exists to avoid. Scoping the id at the boundary means the wrong write is unrepresentable rather than
 * merely discouraged, which is the same reason `buildPlannedTaskLineId` exists for tasks.
 *
 * Standing-only boards (a Service Line's Items tab) address rows by the record id directly; there is no
 * Visit to scope against and no recurring row to protect.
 */
const PLANNED_ITEM_ID_PREFIX = 'planned-item.';

export function buildPlannedItemLineId(visitId: string, itemId: string): string {

    return `${PLANNED_ITEM_ID_PREFIX}${encodeURIComponent(JSON.stringify([visitId, itemId]))}`;
}

export function readPlannedItemLineId(id: string): [string, string] | null {

    if (!id?.startsWith(PLANNED_ITEM_ID_PREFIX)) return null;
    try {
        const parts: unknown = JSON.parse(decodeURIComponent(id.slice(PLANNED_ITEM_ID_PREFIX.length)));
        return Array.isArray(parts) && parts.length === 2 && parts.every(part => typeof part === 'string' && !!part) ? parts as [string, string] : null;
    } catch { return null; }
}

export const PLANNED_ITEM_PARENT_TYPES: readonly PlannedItemParentType[] = [ActivityEntityName.ServiceLine, ActivityEntityName.ServiceLineTask, ActivityEntityName.ServiceLineTemplate, ActivityEntityName.ServiceLineTaskTemplate];

export function isPlannedItemParentType(value: unknown): value is PlannedItemParentType {

    return PLANNED_ITEM_PARENT_TYPES.includes(value as PlannedItemParentType);
}

export function isTemplateParent(parent: PlannedItemParent): boolean {

    return parent.type === ActivityEntityName.ServiceLineTemplate || parent.type === ActivityEntityName.ServiceLineTaskTemplate;
}
